import React from 'react';
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from 'omaha-design-system';
import { Clock } from '@phosphor-icons/react';
import { useHistory } from 'react-router-dom';
import { DisplayGame } from '../data/types';
import { PROPS_OPEN_COPY, formatPropsOpenTime } from '../data/propsAvailability';
import MatchupHeader from './MatchupPage/MatchupHeader';

const fontFamily = '"Instrument Sans", sans-serif';

const DataHubLink: React.FC<{ league: string }> = ({ league }) => {
  const history = useHistory();
  return (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
      <Typography sx={{ fontFamily, fontSize: '13.33px', lineHeight: 1.5, color: 'rgba(242, 242, 243, 0.7)' }}>
        {PROPS_OPEN_COPY.dfsPrompt}
      </Typography>
      <Button
        size="small"
        variant="text"
        onClick={() => history.push(`/datahub/${league}`)}
        sx={{ fontFamily, fontSize: '13.33px', fontWeight: 600, textTransform: 'none', color: '#42A5F5', p: 0, minWidth: 0, '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
      >
        {PROPS_OPEN_COPY.dfsLinkText}
      </Button>
    </Stack>
  );
};

const Explainer: React.FC<{ title: string; detail?: string; league: string }> = ({ title, detail, league }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: '13px',
      padding: '13px',
      borderRadius: '6.67px',
      border: '1px solid #42A5F5',
      backgroundColor: '#1C1C20',
      maxWidth: 640,
    }}
  >
    <Clock size={20} weight="regular" color="#42A5F5" style={{ flexShrink: 0, marginTop: 1 }} />
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '13px', lineHeight: 1.5, color: '#F2F2F3' }}>
        {title}
      </Typography>
      <Typography sx={{ fontFamily, fontWeight: 400, fontSize: '13.33px', lineHeight: 1.5, color: 'rgba(242, 242, 243, 0.7)' }}>
        {PROPS_OPEN_COPY.body}
      </Typography>
      {detail && (
        <Typography sx={{ fontFamily, fontWeight: 400, fontSize: '13.33px', lineHeight: 1.5, color: 'rgba(242, 242, 243, 0.7)' }}>
          {detail}
        </Typography>
      )}
      <DataHubLink league={league} />
    </Box>
  </Box>
);

interface PropsOpenExplainerProps {
  league: string;
  /** The soonest game to open, if any (undefined when the slate is empty). */
  nextOpen?: { game: DisplayGame; openTime: Date };
}

/** Shown in place of the Top Bets table when no game on the slate is open yet. */
export const PropsOpenExplainer: React.FC<PropsOpenExplainerProps> = ({ league, nextOpen }) => {
  const detail = nextOpen
    ? PROPS_OPEN_COPY.nextUp(
        `${nextOpen.game.awayTeam.abbr} @ ${nextOpen.game.homeTeam.abbr}`,
        nextOpen.game.time,
        formatPropsOpenTime(nextOpen.openTime)
      )
    : undefined;
  return (
    <Box sx={{ px: { xs: 2, md: 0 }, py: { xs: 2, md: 1 } }}>
      <Explainer title={PROPS_OPEN_COPY.allLockedTitle(league)} detail={detail} league={league} />
    </Box>
  );
};

interface LockedMatchupStateProps {
  game: DisplayGame;
  openTime: Date;
  league: string;
  onBackToOpenGames?: () => void;
}

/** Full-page state for a selected game whose props haven't opened: the usual matchup header + explainer. */
export const LockedMatchupState: React.FC<LockedMatchupStateProps> = ({ game, openTime, league, onBackToOpenGames }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const matchup = `${game.awayTeam.abbr} @ ${game.homeTeam.abbr}`;

  return (
    <Stack spacing={isMobile ? 2 : 3}>
      {/* Same header as an open game, without its action buttons */}
      <MatchupHeader game={game} hideActions />

      <Box sx={{ px: isMobile ? 2 : 0 }}>
        <Explainer title={PROPS_OPEN_COPY.lockedGameTitle(matchup, formatPropsOpenTime(openTime))} league={league} />
      </Box>

      {onBackToOpenGames && (
        <Box sx={{ px: isMobile ? 2 : 0 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onBackToOpenGames}
            sx={{
              borderColor: 'rgba(242, 242, 243, 0.38)',
              color: 'text.primary',
              textTransform: 'none',
              fontSize: '11.11px',
              fontWeight: 600,
              py: 0.75,
              px: 1,
              borderRadius: '5.555px',
              '&:hover': {
                borderColor: 'rgba(242, 242, 243, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            See open games
          </Button>
        </Box>
      )}
    </Stack>
  );
};
